import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Download, Play, ExternalLink, Users, 
  FileText, BarChart3, Copy, RefreshCw, Trash2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SurveyResponsesPage = () => {
  const { projectId, surveyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/surveys/${surveyId}/responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurvey(res.data.survey);
      setResponses(res.data.responses);
    } catch (err) {
      toast.error('Failed to load survey responses');
      navigate(`/project/${projectId}/data-collection`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [surveyId, projectId, token, navigate]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/surveys/${surveyId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey_${surveyId.slice(0, 8)}_responses.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const handleStartAnalysis = () => {
    // Navigate to project analysis page with dataset preloaded
    navigate(`/project/${projectId}?from=survey&surveyId=${surveyId}`);
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/survey/${survey.share_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Survey link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}/data-collection`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">{survey?.title}</h1>
                <p className="text-sm text-slate-500">Survey Responses</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => fetchData()}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              {responses.length > 0 && (
                <>
                  <Button variant="outline" onClick={handleExport}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button onClick={handleStartAnalysis} className="bg-indigo-600 hover:bg-indigo-700">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Start Analysis
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{responses.length}</p>
                <p className="text-sm text-slate-500">
                  {survey?.target_responses ? `of ${survey.target_responses}` : ''} Responses
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{survey?.questions?.length || 0}</p>
                <p className="text-sm text-slate-500">Questions</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                survey?.status === 'active' ? 'bg-green-100' : 'bg-slate-100'
              }`}>
                <Play className={`w-5 h-5 ${survey?.status === 'active' ? 'text-green-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900 capitalize">{survey?.status}</p>
                <p className="text-sm text-slate-500">Status</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ExternalLink className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <Button variant="link" onClick={copyShareLink} className="p-0 h-auto text-purple-600">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy Link
                </Button>
                <p className="text-sm text-slate-500">Share Survey</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {survey?.target_responses && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Collection Progress</span>
              <span className="text-sm text-slate-500">
                {Math.round((responses.length / survey.target_responses) * 100)}%
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className="bg-indigo-600 h-3 rounded-full transition-all"
                style={{ width: `${Math.min((responses.length / survey.target_responses) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Responses Table */}
        {responses.length > 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-700">Submitted At</th>
                    {survey?.questions?.slice(0, 5).map((q, i) => (
                      <th key={i} className="px-4 py-3 text-left text-sm font-medium text-slate-700 max-w-[200px] truncate">
                        {q.question}
                      </th>
                    ))}
                    {(survey?.questions?.length || 0) > 5 && (
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-500">
                        +{survey.questions.length - 5} more
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {responses.map((response, idx) => (
                    <tr key={response.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {new Date(response.submitted_at).toLocaleString()}
                      </td>
                      {survey?.questions?.slice(0, 5).map((q, i) => {
                        let answer = response.answers[q.id];
                        if (typeof answer === 'object') {
                          answer = JSON.stringify(answer);
                        }
                        return (
                          <td key={i} className="px-4 py-3 text-sm text-slate-700 max-w-[200px] truncate">
                            {answer || '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No responses yet</h3>
            <p className="text-slate-500 mb-4">Share your survey link to start collecting responses</p>
            {survey?.status === 'active' && (
              <Button onClick={copyShareLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Survey Link
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SurveyResponsesPage;
